/**
 * scoringAspects — coach/athlete-facing view of what the platform measures.
 *
 * Reads `schemas/scoring_aspects.json` at the REPO ROOT directly, on purpose:
 * that file is the single source of truth, also consumed by the backend, whose
 * stitch tests pin every factual claim in it to live scoring config
 * (backend/tests/test_scoring_aspects_registry.py). Hand-mirroring it into
 * TypeScript — this repo's usual convention for backend contracts — would
 * recreate exactly the two-vocabularies drift that produced a silent
 * zero-rules bug in the rule compiler (2026-08-09), and would defeat the
 * registry's entire reason for existing.
 *
 * Nothing here invents copy. If a description is wrong, fix the JSON.
 */

import registry from '../../../schemas/scoring_aspects.json';
import type { StructuredGuideBody } from './guideContentTypes';

export type AspectRole = 'score' | 'zero_class' | 'display' | 'diagnostic';

export interface ScoringAspect {
  id: string;
  name: string;
  coach_description: string;
  athlete_description: string;
  measures: string;
  landmark_scope: string;
  landmark_scope_note?: string;
  weight_source: string;
  weight_note?: string;
  role: AspectRole;
  status: string;
  interactions?: string[];
}

export const SCORING_ASPECTS: ScoringAspect[] = registry.aspects as ScoringAspect[];
export const SCORING_ASPECTS_VERSION: string = registry.version;

/** Aspects that move the overall score, in registry order. */
export const SCORED_ASPECTS = SCORING_ASPECTS.filter((a) => a.role === 'score');

/** Shown but deliberately not part of the score (e.g. Timing). */
export const DIAGNOSTIC_ASPECTS = SCORING_ASPECTS.filter((a) => a.role === 'diagnostic');

export function getAspect(id: string): ScoringAspect | undefined {
  return SCORING_ASPECTS.find((a) => a.id === id);
}

/**
 * Build the "what is measured" guide body for a given audience.
 *
 * Coach framing leads with the distinction that actually resolves confusion:
 * some aspects only see landmarks you annotated, others always watch the whole
 * body. Athlete framing leads with what each number means for them.
 */
export function buildScoringAspectsBody(audience: 'coach' | 'athlete'): StructuredGuideBody {
  const describe = (a: ScoringAspect) =>
    audience === 'coach' ? a.coach_description : a.athlete_description;

  const points = SCORING_ASPECTS.filter((a) => a.role !== 'display').map((a) => {
    const scope =
      audience === 'coach' && a.landmark_scope_note ? ` ${a.landmark_scope_note}` : '';
    return `${a.name}: ${describe(a)}${scope}`;
  });

  const lead =
    audience === 'coach'
      ? 'Your score comes from a few different measurements. The important difference between them is what each one looks at.'
      : 'Your score comes from a few different measurements, each looking at something different.';

  return { lead, points };
}
